import { Injectable, inject } from '@angular/core';

import { LogService, ScopedLogger } from '@usheru-hff/shared/utils';

export interface HeaderDebugState {
  routerUrl: string;
  locationPath: string;
  hostBindingEnabled: boolean;
  trackScrollEnabled: unknown;
  hasScrolled: boolean | undefined;
  isFixed: boolean | undefined;
  scrolledStatus: 'top' | 'scrolled' | 'initial' | null | undefined;
  scrollDirection: 'up' | 'down' | null | undefined;
  overContentLg: boolean | undefined;
  overContentSm: boolean | undefined;
  isOverContentAtTopLg: boolean | undefined;
  isOverContentAtTopSm: boolean | undefined;
  overContentScrolledLg: unknown;
  overContentScrolledSm: unknown;
  logoFilter: string | undefined;
  hasDifferentScrolledClass: boolean | undefined;
  headerCustomizationClass: string;
}

interface HeaderLogSnapshotParams {
  phase: string;
  isBrowser: boolean;
  host: HTMLElement;
  state: HeaderDebugState;
  extra?: Record<string, unknown>;
}

@Injectable({ providedIn: 'root' })
export class HeaderDebugProbe {
  private readonly logger: ScopedLogger = inject(LogService).scoped('header');

  logSnapshot({ phase, isBrowser, host, state, extra = {} }: HeaderLogSnapshotParams): void {
    if (!isBrowser) {
      this.logger.log('headerState', {
        phase,
        isBrowser: false,
        ...state,
        ...extra
      });
      return;
    }

    const wrapper = host.querySelector('.header-wrapper') as HTMLElement | null;
    this.logger.log('headerState', {
      phase,
      isBrowser,
      ...state,
      host: this.elementMetrics(host),
      wrapper: this.wrapperMetrics(wrapper),
      ...extra
    });
  }

  logSnapshotOnFrames(isBrowser: boolean, capture: (phase: string) => void, phase: string): void {
    if (!isBrowser) return;

    requestAnimationFrame(() => {
      capture(`${phase}_raf1`);
      requestAnimationFrame(() => capture(`${phase}_raf2`));
    });
  }

  createMutationObserver(
    isBrowser: boolean,
    target: HTMLElement | null,
    onMutation: (records: MutationRecord[]) => void
  ): MutationObserver | null {
    if (!isBrowser || !target || typeof globalThis.MutationObserver !== 'function') return null;

    const observer = new MutationObserver(records => onMutation(records));
    observer.observe(target, {
      attributes: true,
      attributeFilter: ['class', 'style', 'data-scroll-position', 'data-scroll-direction', 'data-logo-filter']
    });
    return observer;
  }

  private elementMetrics(el: HTMLElement | null): Record<string, unknown> | null {
    if (!el) return null;

    const rect = el.getBoundingClientRect();
    return {
      className: el.className || null,
      dataScrollPosition: el.getAttribute('data-scroll-position'),
      dataScrollDirection: el.getAttribute('data-scroll-direction'),
      dataLogoFilter: el.getAttribute('data-logo-filter'),
      dataOverContentLg: el.getAttribute('data-over-content-lg'),
      dataOverContentScrolledLg: el.getAttribute('data-over-content-scrolled-lg'),
      rectHeight: Number(rect.height.toFixed(2)),
      rectWidth: Number(rect.width.toFixed(2))
    };
  }

  private wrapperMetrics(el: HTMLElement | null): Record<string, unknown> | null {
    if (!el) return null;

    const computed = globalThis.getComputedStyle(el);
    const rect = el.getBoundingClientRect();
    return {
      className: el.className || null,
      rectHeight: Number(rect.height.toFixed(2)),
      rectWidth: Number(rect.width.toFixed(2)),
      background: computed.background || null,
      backgroundColor: computed.backgroundColor || null,
      boxShadow: computed.boxShadow || null,
      animationName: computed.animationName || null,
      animationDuration: computed.animationDuration || null,
      transform: computed.transform || null,
      headerBackgroundColorVar: computed.getPropertyValue('--header-background-color').trim() || null,
      headerOverContentBackgroundColorVar:
        computed.getPropertyValue('--header-over-content-background-color').trim() || null
    };
  }
}
